import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ReviewForm } from '../../shared/models/review-form/review-form.model';
import Enumerable from 'linq';
import { ReviewService } from '../../core/services/review.service';
import { forkJoin } from 'rxjs';
import { ReviewFormService } from '../../core/services/review-form.service';
import { ProjectPerformanceReviewStatuses } from '../../shared/models/pprs/project-performance-review-statuses.enum';
import { ToastService } from '../../core/services/toast-service';
import { ToastMode } from '../../shared/models/toast/toast-mode.enum';
import { formatDate } from '@angular/common';
import { ReviewFormSection } from '../../shared/models/review-form/review-form-section.model';
import { ProjectPerformanceReview } from '../../shared/models/pprs/project-performance-review.model';
import { ReviewFormLineItem } from '../../shared/models/review-form/review-form-line-item.model';
import { EmployeeCacheService } from '../../core/services/employee-cache.service';
import { EmployeePermissionType } from '../../shared/models/employee/employee-permissions-type.model';

@Component({
  selector: 'app-review',
  templateUrl: './review.component.html',
  styleUrl: './review.component.css'
})
export class ReviewComponent implements OnInit {
  public title: string = '';

  public id: string = '';

  public reviewForm: ReviewForm | null = null;

  public readOnly: boolean = true;

  public status: string = '';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private reviewService: ReviewService,
    private reviewFormService: ReviewFormService,
    private employeeCacheService: EmployeeCacheService,
    private toastService: ToastService
  ) { }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      this.id = params['id'] ?? '';
      if (this.id == '') {
        this.toastService.show('Error: Missing review Id.', ToastMode.Danger);
        this.router.navigate(['/reviews']);
        return;
      }

      this.loadData();
    });
  }

  private loadData = () => forkJoin([
    this.reviewService.getReview(this.id),
    this.reviewFormService.getReview(this.id)
  ]).subscribe({
    next: ([
      review,
      reviewForm
    ]) => {
      const email = this.employeeCacheService.email.toLowerCase();
      const isAssigned = review.assignedTo?.some(x => x?.toLowerCase() === email) ?? false;
      // Open individual PPRs lets someone view an in progress PPR that isn't assigned to them, read only
      const isUnassignedInProgress = (review.status == 'In Progress' || review.status == 'Not Started') && !isAssigned;
      if ((isUnassignedInProgress && !this.employeeCacheService.checkPermissions([EmployeePermissionType.CanOpenIndividualPPRs]))
        || (review.status == 'Review' && !this.employeeCacheService.checkPermissions([EmployeePermissionType.SuperAdmin]))) {
        this.toastService.show('You do Not have permission to access this page', ToastMode.Danger);
        this.router.navigate(['/reviews']);
      }

      const issuedDate = !review?.issuedDate
        ? new Date()
        : new Date(review?.issuedDate);
      this.title = `${review?.projectName} (${review.projectId}) - ${formatDate(issuedDate, 'MMMM yyyy', 'en-US')} Review`;
      this.readOnly = review?.status === ProjectPerformanceReviewStatuses.Completed || isUnassignedInProgress;
      this.status = review?.status ?? ProjectPerformanceReviewStatuses.NotStarted;

      // Note: Overwrite the 'none' type to not be required
      Enumerable.from(reviewForm?.sections || [])
        .selectMany(x => x?.lineItems ?? [])
        .where(x => x.type == 'none')
        .forEach(x => {
          x.conditions = [];
          x.flags = [];
          x.required = false;
        });

      Enumerable.from(reviewForm?.sections || [])
        .selectMany(x => x?.lineItems ?? [])
        .join(Enumerable.from(review?.answers ?? []), x => x.id, y => y.id, (x, y) => ({ question: y, lineItem: x }))
        .forEach(x => {
          x.lineItem.value = x.question.value;
        });

      // Add in the project detail section
      reviewForm.sections = [this.detailSection(review)].concat(reviewForm.sections);

      this.reviewForm = reviewForm;
    },
    error: (err) => {
      this.toastService.show('Error: Unable to load review information.', ToastMode.Danger);
      this.router.navigate(['/reviews']);
      console.log(err);
    }
  });

  private detailSection = (review: ProjectPerformanceReview) => ({
    label: 'Project Details',
    lineItems: Enumerable.from(
      [
        { property: 'issuedDate', label: 'Issued Date', format: 'MMMM yyyy' },
        { property: 'status', label: 'Status' },
        { property: 'projectName', label: 'Project Name' },
        { property: 'projectId', label: 'Project Number' },
        { property: 'location', label: 'Location' },
        { property: 'submittedBy', label: 'Submitted By' },
        { property: 'architectEngineer', label: 'Architect Engineer' },
        { property: 'companyNumber', label: 'Company Number' },
        { property: 'contractAmount', label: 'Contract Amount', prefix: '$' },
        { property: 'contractType', label: 'Contract Type' },
        { property: 'govtAgency', label: 'Government Agency' },
        { property: 'owner', label: 'Owner' },
        { property: '__userName', label: 'Last Updated By' },
        { property: '__timestamp', label: 'Last Updated Date', format: 'MM/dd/yyyy' },
      ])
      .select((x, i) => this.detailLineItem(i, review, x.property, x.label, x.prefix, x.format))
      .where(x => x.value != '')
      .toArray(),
    id: 0
  } as ReviewFormSection);

  private detailLineItem = (index: number, section: ProjectPerformanceReview, property: string, label: string, prefix: string | undefined, format: string | undefined) => {
    var lineItem = {
      required: false,
      label: label,
      id: index,
      type: 'detail',
      value: ''
    } as ReviewFormLineItem;

    var value = `${section[property as keyof typeof section] ?? ''}`;
    if (!value) {
      return lineItem;
    }

    format ??= '';
    if (format.length > 0) {
      value = this.dateFormat(value, format);
    }

    lineItem.value = `${prefix ?? ''}${value}`;
    return lineItem;
  }

  private dateFormat = (value: string, format: string) => formatDate(new Date(value), format, 'en-US');
}
